import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFlexLayout, useTable } from 'react-table';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  WindowScroller,
} from 'react-virtualized';
import { injectIntl } from 'react-intl';
import classnames from 'classnames';

import intlShape from '@/shared/utils/intlShape';
import { defaultColumn, getColumns } from './PerformanceBalanceHelpers';

const getStyles = (props, align = 'left') => [
  props,
  {
    style: {
      textAlign: align,
    },
  },
];
const headerProps = (props, { column }) => getStyles(props, column.align);
const headerGroupProps = props => getStyles(props, 'center');
const cellProps = (props, { cell }) => getStyles(props, cell.column.align);

const cache = new CellMeasurerCache({
  defaultHeight: 37,
  minHeight: 37,
  fixedWidth: true,
});

const PerformanceBalanceVirtualTable = ({
  data,
  intl,
  showAssignments,
  totals,
  reportFilters,
  selectedRelationId,
  onRowClick,
}) => {
  const columns = useMemo(() => getColumns(intl, showAssignments, totals, reportFilters), [
    intl,
    showAssignments,
    totals,
    reportFilters,
  ]);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    totalColumnsWidth,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useFlexLayout,
  );

  useEffect(() => {
    cache.clearAll();
  }, [data]);

  const RenderRow = useCallback(
    ({ index, style, parent, key }) => {
      const row = rows[index];
      prepareRow(row);
      const isHighlighted = row.original.relationId === selectedRelationId;
      return (
        <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
          <div
            {...row.getRowProps({
              style,
              className: classnames('tr', { 'row-highlighted': isHighlighted }),
            })}
            onClick={() => onRowClick(row.original.relationId)}
          >
            {row.cells.map(cell => (
              <div {...cell.getCellProps(cellProps)} className="td">
                {cell.render('Cell')}
              </div>
            ))}
          </div>
        </CellMeasurer>
      );
    },
    [prepareRow, rows, selectedRelationId, onRowClick],
  );

  return (
    <div
      {...getTableProps(props => [
        props,
        { className: 'table table-report relation-rentability margin50B' },
      ])}
    >
      <div className="thead sticky-header">
        {headerGroups.map(headerGroup => (
          <div {...headerGroup.getHeaderGroupProps(headerGroupProps)} className="tr">
            {headerGroup.headers.map(column => (
              <div
                {...column.getHeaderProps(headerProps)}
                className="th"
                title={column.headerTitle}
              >
                {column.render('Header')}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="tfoot">
        {footerGroups.map(group => (
          <div {...group.getFooterGroupProps(headerGroupProps)} className="tr bold">
            {group.headers.map(column => (
              <div {...column.getFooterProps(headerProps)} className="th">
                {column.render('Footer')}
              </div>
            ))}
          </div>
        ))}
      </div>
      <WindowScroller>
        {({ height, isScrolling, registerChild, scrollTop, onChildScroll }) => (
          <AutoSizer disableHeight>
            {({ width: containerWidth }) => (
              <div {...getTableBodyProps()} className="tbody" ref={registerChild}>
                <List
                  autoHeight
                  height={height}
                  isScrolling={isScrolling}
                  rowCount={rows.length}
                  rowHeight={cache.rowHeight}
                  rowRenderer={RenderRow}
                  onScroll={onChildScroll}
                  scrollTop={scrollTop}
                  width={Math.max(totalColumnsWidth, containerWidth)}
                />
              </div>
            )}
          </AutoSizer>
        )}
      </WindowScroller>
    </div>
  );
};

PerformanceBalanceVirtualTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showAssignments: PropTypes.bool.isRequired,
  totals: PropTypes.shape({}).isRequired,
  reportFilters: PropTypes.shape({}).isRequired,
  selectedRelationId: PropTypes.string,
  onRowClick: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
};

PerformanceBalanceVirtualTable.defaultProps = {
  selectedRelationId: null,
};

export default injectIntl(PerformanceBalanceVirtualTable);
